import { detectDevice } from './deviceDetect'

const permissionState = async (name) => {
  try {
    const status = await navigator.permissions.query({ name })
    return status.state
  } catch {
    return 'unknown' // Firefox and Safari do not expose every permission name
  }
}

// Briefly open the camera and microphone to prove they work, then release them.
const testMediaAccess = async () => {
  const result = {}
  for (const [kind, constraints] of [['camera', { video: true }], ['microphone', { audio: true }]]) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      const track = stream.getTracks()[0]
      result[kind] = { ok: true, device: track?.label || null, settings: kind === 'camera' ? track?.getSettings?.() : undefined }
      stream.getTracks().forEach(t => t.stop())
    } catch (e) {
      // NotAllowedError = blocked, NotFoundError = no device, NotReadableError = in use by another app
      result[kind] = { ok: false, error: e.name, message: e.message }
    }
  }
  return result
}

/**
 * Collect what the support agent cannot see from the server: browser, permissions,
 * media devices and network. Device labels are only included once permission is granted.
 */
export const runBrowserDiagnostics = async ({ testMedia = false, socketConnected } = {}) => {
  const conn = navigator.connection
  const [camera, microphone] = await Promise.all([permissionState('camera'), permissionState('microphone')])
  const mediaTest = testMedia && navigator.mediaDevices?.getUserMedia ? await testMediaAccess() : undefined

  let devices = []
  try {
    devices = await navigator.mediaDevices.enumerateDevices()
  } catch { /* mediaDevices unavailable outside secure contexts */ }
  const byKind = (kind) => devices.filter(d => d.kind === kind).map(d => d.label || '(name hidden until permission is granted)')

  return {
    browser: {
      userAgent: navigator.userAgent,
      device: detectDevice(),
      language: navigator.language,
      online: navigator.onLine,
      secureContext: window.isSecureContext,
      screen: `${window.screen.width}x${window.screen.height}`
    },
    permissions: { camera, microphone },
    mediaDevices: {
      cameras: byKind('videoinput'),
      microphones: byKind('audioinput'),
      speakers: byKind('audiooutput')
    },
    mediaTest,
    webrtcSupported: typeof window.RTCPeerConnection === 'function',
    network: conn
      ? { effectiveType: conn.effectiveType, downlinkMbps: conn.downlink, rttMs: conn.rtt, saveData: conn.saveData }
      : 'not reported by this browser',
    realtimeConnection: socketConnected ? 'connected' : 'disconnected'
  }
}
